import { formatTargetMonth } from '@/lib/trip-dates';
import Link from '@/lib/nav/link';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter, usePathname, useSearchParams } from '@/lib/nav/navigation';
import { CalendarIcon as CalendarDays } from '@/components/icons/line/calendar';
import { ConfirmedIcon as Check } from '@/components/icons/line/confirmed';
import { ExpandIcon as ChevronDown } from '@/components/icons/line/expand';
import { LocationIcon as MapPin } from '@/components/icons/line/location';
import { AddIcon as Plus } from '@/components/icons/line/add';
import { TripDockMark } from '@/features/trip/FloatingTripBar';
import { PHONE_FLOATING_BAR_INNER_HIT_CLASS } from '@/features/trip/phoneFloatingBar';
import { cn } from '@/lib/utils';

export interface TripOption {
  id: string;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  /** A month the traveller named without exact days ("2026-11"). */
  targetMonth?: string | null;
  status?: string | null;
  destinations?: string[];
}

interface Props {
  trips: TripOption[];
  currentTripId: string | null;
  variant?: 'topbar' | 'sidebar' | 'bar';
  /**
   * Set while the user is on the new-trip flow. The switcher then shows a
   * neutral "New trip" state instead of labeling an existing trip as current
   * (the existing trips stay in the dropdown so they can switch back).
   */
  pendingNewTrip?: boolean;
  onNavigate?: () => void;
}

/**
 * Trip switcher rendered in the top bar on trip-context pages. Shows the
 * current trip name with a chevron; clicking opens a dropdown list of
 * the user's trips with the active one checked. Bottom of the dropdown
 * has an "Add new trip" link.
 *
 * Picking a different trip routes to the same logical surface as the
 * current page (chat → chat, itinerary → itinerary). Falls back to the
 * itinerary view if the destination route doesn't make sense.
 */
export function TripSwitcher({
  trips,
  currentTripId,
  variant = 'topbar',
  pendingNewTrip = false,
  onNavigate,
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState<{ left: number; bottom: number; width: number } | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isBar = variant === 'bar';

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const place = () => {
      if (!isBar) return;
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(320, window.innerWidth - 24);
      const left = Math.min(Math.max(12, rect.left), Math.max(12, window.innerWidth - width - 12));
      setMenuBox({ left, width, bottom: window.innerHeight - rect.top + 8 });
    };
    place();
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, isBar]);

  const current = trips.find((t) => t.id === currentTripId) ?? null;

  // If we don't have any trips yet (rare on this surface — but possible
  // mid-state) the switcher renders as a static "New Trip" CTA.
  if (trips.length === 0) {
    return (
      <Link
        href="/new-trip"
        onClick={onNavigate}
        className={
          isBar
            ? 'flex h-full min-w-0 flex-1 items-center gap-2.5 px-1.5 text-left text-[13px] font-bold text-ink'
            : 'inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-bold text-primary-foreground transition hover:bg-primary/90'
        }
      >
        {isBar ? (
          <TripDockMark tone="trip">
            <Plus className="h-4 w-4" strokeWidth={2} />
          </TripDockMark>
        ) : (
          <Plus className="h-3.5 w-3.5" />
        )}
        {isBar ? 'New trip' : 'Start your first trip'}
      </Link>
    );
  }

  const switchTo = (tripId: string) => {
    setOpen(false);
    onNavigate?.();
    const isAlreadyOnTrip = pathname.startsWith(`/trips/${tripId}`);
    if (tripId === currentTripId && isAlreadyOnTrip) return;
    // Map the current path to the equivalent on the new trip — chat stays
    // chat, dashboard stays dashboard, sleep stays sleep, fall back to itinerary view.
    const isChat = pathname.endsWith('/chat');
    const isDashboard = pathname.endsWith('/dashboard') || pathname.endsWith('/details');
    const isSleep = pathname.endsWith('/sleep');
    if (isChat) router.push(`/trips/${tripId}/chat`);
    else if (isDashboard)
      router.push(
        `/trips/${tripId}/details?tool=${encodeURIComponent(searchParams.get('tool') ?? 'details')}`,
      );
    else if (isSleep) router.push(`/trips/${tripId}/sleep`);
    else {
      const view = searchParams.get('view');
      router.push(
        view && ['overview', 'itinerary', 'map'].includes(view)
          ? `/trips/${tripId}?view=${view}`
          : `/trips/${tripId}`,
      );
    }
  };

  const isSidebar = variant === 'sidebar';
  const groupedTrips = groupTrips(trips, currentTripId);
  const tripLabel = pendingNewTrip
    ? isSidebar
      ? 'Setting up your trip'
      : 'New trip'
    : (current?.name ?? (isSidebar ? 'Switch trip' : 'Pick a trip'));
  const menu = (
    <div
      ref={menuRef}
      role="menu"
      className={
        isBar
          ? 'pointer-events-auto fixed z-40 overflow-hidden rounded-2xl border border-ink/10 bg-white shadow-lg'
          : isSidebar
            ? 'absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-lg border border-ink/10 bg-white shadow-lg'
            : 'absolute left-1/2 top-full z-30 mt-2 w-64 -translate-x-1/2 overflow-hidden rounded-lg border border-ink/10 bg-white shadow-lg'
      }
      style={
        isBar && menuBox
          ? { left: menuBox.left, bottom: menuBox.bottom, width: menuBox.width }
          : undefined
      }
    >
      <ul role="listbox" className="max-h-80 overflow-auto py-1">
        {groupedTrips.map((group) => (
          <li key={group.label}>
            <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wide text-ink/40">
              {group.label}
            </p>
            <ul>
              {group.trips.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => switchTo(t.id)}
                    className={cn(
                      'flex w-full items-start justify-between gap-2 px-3 py-2.5 text-left transition',
                      t.id === currentTripId
                        ? 'bg-primary/5 text-primary'
                        : 'text-ink hover:bg-muted',
                    )}
                    role="option"
                    aria-selected={t.id === currentTripId}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold">{t.name}</span>
                      <span className="mt-1 flex min-w-0 items-center gap-1 text-xs text-ink/55">
                        <CalendarDays className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{formatDateRange(t)}</span>
                      </span>
                      {destinationSummary(t) ? (
                        <span className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-ink/55">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{destinationSummary(t)}</span>
                        </span>
                      ) : null}
                    </span>
                    {t.id === currentTripId ? (
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <Link
        href="/new-trip"
        onClick={() => {
          setOpen(false);
          onNavigate?.();
        }}
        className="flex items-center gap-2 border-t border-ink/5 px-3 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/5"
      >
        <Plus className="h-4 w-4" />
        Add new trip
      </Link>
    </div>
  );

  return (
    <div ref={containerRef} className={isBar ? 'relative flex h-full min-w-0 flex-1' : 'relative'}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={
          isBar
            ? cn(
                'flex h-full w-full min-w-0 items-center gap-2.5 px-1.5 text-left',
                PHONE_FLOATING_BAR_INNER_HIT_CLASS,
              )
            : isSidebar
              ? cn(
                  'flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-sm font-bold transition',
                  current
                    ? 'border-primary/20 bg-primary/5 text-primary hover:bg-primary/10'
                    : 'border-ink/10 bg-white text-ink/70 hover:bg-muted',
                )
              : 'inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-bold text-primary-foreground transition hover:bg-primary/90'
        }
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={isBar ? `Current trip, ${tripLabel}` : undefined}
      >
        {isBar ? (
          <>
            <TripDockMark tone="trip">
              <MapPin className="h-4 w-4" strokeWidth={2} />
            </TripDockMark>
            <span className="line-clamp-2 min-w-0 flex-1 text-[13px] font-bold leading-4 text-ink">
              {tripLabel}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-ink/40" aria-hidden />
          </>
        ) : (
          <>
            <span className="min-w-0">
              {isSidebar && (current || pendingNewTrip) ? (
                <span className="block text-[11px] font-semibold uppercase tracking-wide text-primary/70">
                  {pendingNewTrip ? 'New trip' : 'Current trip'}
                </span>
              ) : null}
              <span className="block max-w-[200px] truncate">{tripLabel}</span>
              {isSidebar && pendingNewTrip ? (
                <span className="mt-0.5 block truncate text-xs font-medium text-ink/50">
                  Switch to an existing trip below
                </span>
              ) : isSidebar && current ? (
                <span className="mt-0.5 block truncate text-xs font-medium text-ink/50">
                  {tripMetaLine(current)}
                </span>
              ) : null}
            </span>
            <ChevronDown className="h-3.5 w-3.5" />
          </>
        )}
      </button>
      {open && isBar && menuBox ? createPortal(menu, document.body) : null}
      {open && !isBar ? menu : null}
    </div>
  );
}

function groupTrips(trips: TripOption[], currentTripId: string | null) {
  const current = trips.filter((trip) => trip.id === currentTripId);
  const rest = trips.filter((trip) => trip.id !== currentTripId);
  const planning = rest.filter((trip) => (trip.status ?? 'planning') !== 'completed');
  const completed = rest.filter((trip) => trip.status === 'completed');
  return [
    ...(current.length ? [{ label: 'Current', trips: current }] : []),
    ...(planning.length ? [{ label: 'Planning', trips: planning }] : []),
    ...(completed.length ? [{ label: 'Completed', trips: completed }] : []),
  ];
}

function tripMetaLine(trip: TripOption): string {
  const date = formatDateRange(trip);
  const destination = destinationSummary(trip);
  return [date, destination].filter(Boolean).join(' · ') || 'Trip workspace';
}

function destinationSummary(trip: TripOption): string {
  const destinations = trip.destinations ?? [];
  if (destinations.length === 0) return '';
  if (destinations.length === 1) return destinations[0] ?? '';
  return `${destinations[0]} + ${destinations.length - 1} more`;
}

function formatDateRange(trip: TripOption): string {
  if (!trip.startDate && !trip.endDate) {
    // "Dates TBD" for a trip whose traveller told us November reads as if we
    // forgot. Say the month we were given.
    return formatTargetMonth(trip.targetMonth) ?? 'Dates TBD';
  }
  if (trip.startDate && trip.endDate) {
    return `${formatShortDate(trip.startDate)} - ${formatShortDate(trip.endDate)}`;
  }
  return formatShortDate(trip.startDate ?? trip.endDate ?? '');
}

function formatShortDate(value: string): string {
  const [year, month, day] = value.split('-').map((part) => Number(part));
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}
